import { MenuItem, TextField } from '@common/material'
import { TimePicker } from '@common/material/TimePicker'
import { Box, Flex } from '@fundamentals'
import { useState } from 'react'
import {
  AdvancedTimeFilterInternalArguments,
  AdvancedTimeFilterValue,
  advancedTimeFilterOptionConditions,
  advancedTimeFilterOperatorsWithoutArguments,
} from '@core/providers/filters'
import { AdvancedFilterPopover } from './AdvancedFilterPopover'
import moment from 'moment'
import { localizedStrings } from '@core/strings'

type AdvancedTimeFilterProps = {
  filter: AdvancedTimeFilterInternalArguments
  onChange: ({ value, operator }: AdvancedTimeFilterValue) => void
  storeValue: AdvancedTimeFilterValue
  anchorEl: HTMLButtonElement | null
  setAnchorEl: (el: HTMLButtonElement | null) => void
  onUnfocused: () => void
}

const formatTimeValue = (time: string | null) => {
  if (!time) return null
  return moment.utc(
    `${moment().format('YYYY-MM-DD')} ${time}`,
    'YYYY-MM-DD HH:mm',
  )
}

export const AdvancedTimeFilter: React.FC<AdvancedTimeFilterProps> = ({
  filter,
  onChange,
  storeValue,
  anchorEl,
  setAnchorEl,
  onUnfocused,
}) => {
  const [dateCondition, setDateCondition] = useState(
    storeValue?.operator ?? advancedTimeFilterOptionConditions[0].value,
  )
  const hideArguments =
    advancedTimeFilterOperatorsWithoutArguments.includes(dateCondition)

  const timeValue = storeValue?.value?.timeValue as string
  const startTime = storeValue?.value?.startTime as string
  const endTime = storeValue?.value?.endTime as string

  const [timeRange, setTimeRange] = useState(
    startTime && endTime ? [startTime, endTime] : [],
  )

  const handleTimeRangeChange = (index: number, newTime: string | null) => {
    const newRange = [...timeRange]
    newRange[index] = newTime
    setTimeRange(newRange)

    const getValue = () => {
      if (!newRange[0] || !newRange[1]) {
        return {
          startTime: null,
          endTime: null,
        }
      }
      return {
        startTime: newRange[0],
        endTime: newRange[1],
      }
    }

    onChange({
      value: getValue(),
      operator: dateCondition,
    })
  }

  const handleSingleTimeChange = (date: moment.Moment | null) => {
    const formattedTime = date ? moment.utc(date).format('HH:mm') : null
    onChange({
      value: { timeValue: formattedTime },
      operator: dateCondition,
    })
  }

  const isRange = dateCondition === 'IS_BETWEEN'

  return (
    <AdvancedFilterPopover
      anchorEl={anchorEl}
      setAnchorEl={setAnchorEl}
      onUnfocused={onUnfocused}
    >
      <Flex flexDirection='column' p={2}>
        <TextField
          value={dateCondition}
          select
          size='small'
          label={localizedStrings.selectFilterOperator}
          sx={{
            mb: 2,
            minWidth: 200,
          }}
          SelectProps={{
            onChange(e) {
              const operator = e.target
                .value as AdvancedTimeFilterValue['operator']
              setDateCondition(operator)
              setTimeRange([])

              if (
                advancedTimeFilterOperatorsWithoutArguments.includes(operator)
              ) {
                onChange({ value: null, operator })
                return
              }

              if (storeValue?.value?.timeValue && operator !== 'IS_BETWEEN') {
                onChange({
                  value: { timeValue: storeValue.value.timeValue },
                  operator,
                })
              }
            },
          }}
        >
          {advancedTimeFilterOptionConditions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
        {!hideArguments && (
          <Box>
            {isRange ? (
              <Flex gap={1}>
                <TimePicker
                  UTC={filter.UTC}
                  name='test'
                  label={localizedStrings.startTime}
                  value={formatTimeValue(timeRange[0])}
                  onChange={(date) =>
                    handleTimeRangeChange(
                      0,
                      date ? moment.utc(date).format('HH:mm') : null,
                    )
                  }
                />
                <TimePicker
                  UTC={filter.UTC}
                  name='test'
                  label={localizedStrings.endTime}
                  value={formatTimeValue(timeRange[1])}
                  onChange={(date) =>
                    handleTimeRangeChange(
                      1,
                      date ? moment.utc(date).format('HH:mm') : null,
                    )
                  }
                />
              </Flex>
            ) : (
              <TimePicker
                UTC={filter.UTC}
                name='test'
                value={formatTimeValue(timeValue)}
                onChange={handleSingleTimeChange}
              />
            )}
          </Box>
        )}
      </Flex>
    </AdvancedFilterPopover>
  )
}
